import type { NoteName } from '@tunasong/schemas'
import type { ChordType, ChordVariant } from '../chord/chord-types.js'
import * as NoteLib from '../note/note-lib.js'
import { MAJOR_TONICS } from './function-quiz.js'

/**
 * Ear Training Suite v1 (Phase 2 Tier 2) — pure-function core.
 *
 * Two sub-modes: Interval ID (12 ascending melodic intervals, P1..M7) and
 * Chord-quality ID (7 qualities). The octave (P8) is excluded from v1's
 * interval set; cadence-by-ear and progression-by-ear are deferred to v2/v3.
 *
 * Mirrors the Function Quiz analyzer shape file-for-file: pool tables, two
 * question generators with injectable RNG, two validators, two session-stat
 * folds. Tonic selection reuses Function Quiz's MAJOR_TONICS table so the
 * displayed pitch names avoid double-flats / double-sharps.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.1
 * Bowie scope memo: .claude/agent-memory/bowie/project_ear_training_suite_v1_scope.md
 */

/** The 12 melodic intervals quizzed in v1. */
export type IntervalKind = 'P1' | 'm2' | 'M2' | 'm3' | 'M3' | 'P4' | 'TT' | 'P5' | 'm6' | 'M6' | 'm7' | 'M7'

/** The 7 chord qualities quizzed in v1. */
export type ChordQualityKind = 'major' | 'minor' | 'diminished' | 'augmented' | 'dominant-7' | 'major-7' | 'minor-7'

export interface IntervalEntry {
  kind: IntervalKind
  /** 0..11 — chromatic distance from root to target. */
  semitones: number
  lexiconId: string
  displayName: string
  displayNameShort: string
}

export interface QualityEntry {
  kind: ChordQualityKind
  lexiconId: string
  displayName: string
  displayNameShort: string
  /** Drives the ChordVariant builder. */
  variant: ChordType
}

/**
 * Bowie-locked interval table (Bowie §3.2). 12 entries ordered P1..M7;
 * `semitones` is the 0-indexed position so iteration order matches semitone
 * count. The octave (P8 / 12 semitones) is excluded from v1.
 */
export const INTERVAL_POOL: readonly IntervalEntry[] = [
  { kind: 'P1', semitones: 0, lexiconId: 'interval-perfect-unison', displayName: 'Perfect unison', displayNameShort: 'P1' },
  { kind: 'm2', semitones: 1, lexiconId: 'interval-minor-2nd', displayName: 'Minor 2nd', displayNameShort: 'm2' },
  { kind: 'M2', semitones: 2, lexiconId: 'interval-major-2nd', displayName: 'Major 2nd', displayNameShort: 'M2' },
  { kind: 'm3', semitones: 3, lexiconId: 'interval-minor-3rd', displayName: 'Minor 3rd', displayNameShort: 'm3' },
  { kind: 'M3', semitones: 4, lexiconId: 'interval-major-3rd', displayName: 'Major 3rd', displayNameShort: 'M3' },
  { kind: 'P4', semitones: 5, lexiconId: 'interval-perfect-4th', displayName: 'Perfect 4th', displayNameShort: 'P4' },
  { kind: 'TT', semitones: 6, lexiconId: 'interval-tritone', displayName: 'Tritone', displayNameShort: 'TT' },
  { kind: 'P5', semitones: 7, lexiconId: 'interval-perfect-5th', displayName: 'Perfect 5th', displayNameShort: 'P5' },
  { kind: 'm6', semitones: 8, lexiconId: 'interval-minor-6th', displayName: 'Minor 6th', displayNameShort: 'm6' },
  { kind: 'M6', semitones: 9, lexiconId: 'interval-major-6th', displayName: 'Major 6th', displayNameShort: 'M6' },
  { kind: 'm7', semitones: 10, lexiconId: 'interval-minor-7th', displayName: 'Minor 7th', displayNameShort: 'm7' },
  { kind: 'M7', semitones: 11, lexiconId: 'interval-major-7th', displayName: 'Major 7th', displayNameShort: 'M7' },
]

/**
 * Bowie-locked chord-quality table (Bowie §3.3). 7 entries. Half-dim, dim7,
 * aug7, sus and extensions are deferred to v2. `variant` is the `ChordType`
 * that drives the playback's ChordVariant build.
 */
export const QUALITY_POOL: readonly QualityEntry[] = [
  { kind: 'major', lexiconId: 'quality-major', displayName: 'Major', displayNameShort: 'maj', variant: '' },
  { kind: 'minor', lexiconId: 'quality-minor', displayName: 'Minor', displayNameShort: 'min', variant: 'm' },
  { kind: 'diminished', lexiconId: 'quality-diminished', displayName: 'Diminished', displayNameShort: 'dim', variant: 'dim' },
  { kind: 'augmented', lexiconId: 'quality-augmented', displayName: 'Augmented', displayNameShort: 'aug', variant: 'aug' },
  { kind: 'dominant-7', lexiconId: 'quality-dominant-7', displayName: 'Dominant 7', displayNameShort: 'dom7', variant: '7' },
  { kind: 'major-7', lexiconId: 'quality-major-7', displayName: 'Major 7', displayNameShort: 'maj7', variant: 'M7' },
  { kind: 'minor-7', lexiconId: 'quality-minor-7', displayName: 'Minor 7', displayNameShort: 'min7', variant: 'm7' },
]

/** Root octave both sub-modes audition at. (Bowie §3.5) */
const ROOT_OCTAVE = 4

export interface IntervalQuestion {
  rootNote: NoteName
  rootOctave: number
  /** Computed via NoteLib.getNoteByChromaticOffset — simpler-spelled enharmonic by default. */
  targetNote: NoteName
  /** ROOT_OCTAVE or ROOT_OCTAVE+1 depending on chromatic wrap. */
  targetOctave: number
  entry: IntervalEntry
}

export interface QualityQuestion {
  chord: ChordVariant
  rootOctave: number
  entry: QualityEntry
}

/**
 * Generate a fresh random interval question.
 *
 * RNG draw order:
 *  1. `rng()` → root index into `MAJOR_TONICS`.
 *  2. `rng()` → interval index into `INTERVAL_POOL`.
 *
 * Target octave wraps when `getNoteChromaticIndex(root) + semitones >= 12`,
 * so e.g. `G4 + P5 = D5` while `C4 + P5 = G4`.
 */
export const generateIntervalQuestion = (rng: () => number = Math.random): IntervalQuestion => {
  const rootNote = MAJOR_TONICS[Math.floor(rng() * MAJOR_TONICS.length)]
  const entry = INTERVAL_POOL[Math.floor(rng() * INTERVAL_POOL.length)]
  const rootChromatic = NoteLib.getNoteChromaticIndex(rootNote)
  const targetNote = NoteLib.getNoteByChromaticOffset(rootNote, entry.semitones)
  const targetOctave = rootChromatic + entry.semitones >= 12 ? ROOT_OCTAVE + 1 : ROOT_OCTAVE
  return { rootNote, rootOctave: ROOT_OCTAVE, targetNote, targetOctave, entry }
}

/**
 * Generate a fresh random chord-quality question.
 *
 * RNG draw order:
 *  1. `rng()` → root index into `MAJOR_TONICS`.
 *  2. `rng()` → quality index into `QUALITY_POOL`.
 */
export const generateQualityQuestion = (rng: () => number = Math.random): QualityQuestion => {
  const rootNote = MAJOR_TONICS[Math.floor(rng() * MAJOR_TONICS.length)]
  const entry = QUALITY_POOL[Math.floor(rng() * QUALITY_POOL.length)]
  const chord: ChordVariant = { root: rootNote, variant: entry.variant }
  return { chord, rootOctave: ROOT_OCTAVE, entry }
}

export interface IntervalVerdict {
  correct: boolean
  picked: IntervalKind
  entry: IntervalEntry
}

export interface QualityVerdict {
  correct: boolean
  picked: ChordQualityKind
  entry: QualityEntry
}

export const validateIntervalAnswer = (q: IntervalQuestion, picked: IntervalKind): IntervalVerdict => ({
  correct: picked === q.entry.kind,
  picked,
  entry: q.entry,
})

export const validateQualityAnswer = (q: QualityQuestion, picked: ChordQualityKind): QualityVerdict => ({
  correct: picked === q.entry.kind,
  picked,
  entry: q.entry,
})

export interface IntervalSessionResult {
  question: IntervalQuestion
  verdict: IntervalVerdict
}

export interface QualitySessionResult {
  question: QualityQuestion
  verdict: QualityVerdict
}

export interface EarTrainingSessionStats {
  total: number
  correct: number
}

export const computeIntervalSessionStats = (results: IntervalSessionResult[]): EarTrainingSessionStats => ({
  total: results.length,
  correct: results.filter(r => r.verdict.correct).length,
})

export const computeQualitySessionStats = (results: QualitySessionResult[]): EarTrainingSessionStats => ({
  total: results.length,
  correct: results.filter(r => r.verdict.correct).length,
})
